// Bolo3K comms (Wave 2b-3) — canned-message ping wheel + players/alliances
// sheet + incoming feed, mounted in #comms-root (z30). There is NO free-text
// chat on phones: the ping wheel sends short canned lines through the
// ordinary chat wire (ctx.sendChat), so desktop players receive them as
// plain chat — cross-play preserved.
//
// This module cannot subscribe to the Connection itself (main.ts owns the
// handler slots), so incoming traffic is push-driven: the architect wires
// s2c.chat → CommsApi.showIncoming (and any wire-event lines →
// showSystem) at the Wave 3 integration point. Note the server echoes a
// sender's own chat back to them (room.ts handleChat broadcasts to all
// sessions, sender included), so the wheel does NOT locally echo sends —
// the echo arrives through showIncoming like everyone else's.

import type { GameContext } from '../context.js';
import { CommsFeed } from './feed.js';
import { createPingWheel } from './pingWheel.js';
import { createPlayersSheet } from './playersSheet.js';
import './comms.css';

/** How often open sheets re-read ctx.state() (ally bitmaps, roster). */
const REFRESH_MS = 250;

export type CommsTab = 'ping' | 'players';

export interface CommsApi {
  /** Feed a received chat line into the incoming strip. `from` is the
   *  sender's display name (resolve the slot via playerLabel before
   *  calling); scope 'allies' tints the line. */
  showIncoming(from: string, text: string, scope: string): void;
  /** Feed a system/wire-event line (alliance formed, player joined, …). */
  showSystem(text: string): void;
  /** Close any open sheet (e.g. when the lobby takes the screen over). */
  closeSheet(): void;
  /** Remove all DOM, listeners and timers. */
  dispose(): void;
}

export function initComms(ctx: GameContext, root: HTMLElement): CommsApi {
  // ---- incoming feed (always on) ------------------------------------
  const feed = new CommsFeed(root);

  // ---- top button pair ----------------------------------------------
  const buttons = document.createElement('div');
  buttons.className = 'cw-buttons';
  const commsBtn = document.createElement('button');
  commsBtn.type = 'button';
  commsBtn.className = 'cw-btn';
  commsBtn.textContent = 'COMMS';
  const playersBtn = document.createElement('button');
  playersBtn.type = 'button';
  playersBtn.className = 'cw-btn';
  playersBtn.textContent = 'PLAYERS';
  buttons.append(commsBtn, playersBtn);
  root.appendChild(buttons);

  // ---- sheet shell (backdrop + tabbed bottom sheet) ------------------
  const backdrop = document.createElement('div');
  backdrop.className = 'cw-backdrop';
  backdrop.hidden = true;
  root.appendChild(backdrop);

  const sheet = document.createElement('div');
  sheet.className = 'cw-sheet';
  sheet.hidden = true;

  const tabs = document.createElement('div');
  tabs.className = 'cw-tabs';
  const pingTab = document.createElement('button');
  pingTab.type = 'button';
  pingTab.className = 'cw-tab';
  pingTab.textContent = 'COMMS';
  const playersTab = document.createElement('button');
  playersTab.type = 'button';
  playersTab.className = 'cw-tab';
  playersTab.textContent = 'PLAYERS';
  tabs.append(pingTab, playersTab);
  sheet.appendChild(tabs);

  const body = document.createElement('div');
  body.className = 'cw-body';
  sheet.appendChild(body);
  root.appendChild(sheet);

  const closeSheet = (): void => setOpen(null);
  const pingWheel = createPingWheel(ctx, closeSheet);
  const playersSheet = createPlayersSheet(ctx, closeSheet);

  let openTab: CommsTab | null = null;
  let refreshTimer: ReturnType<typeof setInterval> | null = null;

  function setOpen(tab: CommsTab | null): void {
    openTab = tab;
    const open = tab !== null;
    backdrop.hidden = !open;
    sheet.hidden = !open;
    commsBtn.classList.toggle('cw-open', tab === 'ping');
    playersBtn.classList.toggle('cw-open', tab === 'players');
    pingTab.classList.toggle('cw-on', tab === 'ping');
    playersTab.classList.toggle('cw-on', tab === 'players');
    if (open) {
      body.textContent = '';
      const pane = tab === 'ping' ? pingWheel : playersSheet;
      pane.refresh();
      body.appendChild(pane.el);
      if (refreshTimer === null) {
        // Slow poll while open: ally bitmaps / roster can change under us
        // (someone accepts, a player joins) and the sheet must track it.
        refreshTimer = setInterval(() => {
          (openTab === 'ping' ? pingWheel : playersSheet).refresh();
        }, REFRESH_MS);
      }
    } else if (refreshTimer !== null) {
      clearInterval(refreshTimer);
      refreshTimer = null;
    }
  }

  const toggle = (tab: CommsTab) => (): void => setOpen(openTab === tab ? null : tab);
  commsBtn.addEventListener('click', toggle('ping'));
  playersBtn.addEventListener('click', toggle('players'));
  pingTab.addEventListener('click', () => setOpen('ping'));
  playersTab.addEventListener('click', () => setOpen('players'));
  // Outside-tap dismiss: the backdrop covers everything below the sheet
  // while open (including the touch-control zones — deliberate: a modal
  // sheet should not let stray thumbs drive the tank).
  backdrop.addEventListener('pointerdown', closeSheet);

  return {
    showIncoming: (from, text, scope) => feed.showIncoming(from, text, scope),
    showSystem: (text) => feed.showSystem(text),
    closeSheet,
    dispose: () => {
      setOpen(null);
      feed.dispose();
      buttons.remove();
      backdrop.remove();
      sheet.remove();
    },
  };
}
